import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, Film, Check, X, ExternalLink } from "lucide-react";
import { api, uploadMediaFile } from "@/lib/api";
import { BACKEND_BASE } from "@/lib/backend";
import { AdminPageHeader, AdminSubTabs, useAdminQueryParam } from "@/pages/admin/AdminUx";
import { CmsSplitPreview } from "@/pages/admin/CmsLivePreview";

/**
 * Live-site media slots only (aligned with `/` landing + `/about` + global brand).
 * Removed legacy slots that no longer render on the public site.
 */
const SLOTS = [
  {
    key: "media_logo",
    label: "사이트 심볼 (원형 로고)",
    accept: "image",
    icon: ImageIcon,
    group: "브랜드",
    desc: "원형 DS 심볼 PNG. Footer 워드마크·파비콘과 다른 파일입니다.",
    usage: "사이트 헤더 · 관리자 Sidebar 심볼",
    anchor: "/",
  },
  {
    key: "media_footer_wordmark",
    label: "Footer 워드마크",
    accept: "image",
    icon: ImageIcon,
    group: "브랜드",
    desc: "DSITESOLUTION 텍스트 전용 PNG. 사이트 Footer에서 사용합니다.",
    usage: "사이트 Footer",
    anchor: "/",
  },
  {
    key: "media_favicon",
    label: "브라우저 탭 아이콘 (파비콘)",
    accept: "image",
    icon: ImageIcon,
    group: "브랜드",
    desc: "브라우저 탭·북마크 아이콘. 32×32 이상 권장.",
    usage: "브라우저 탭 아이콘",
    anchor: "/",
  },
  {
    key: "media_main_hero_video",
    label: "현재 메인 Hero 영상",
    accept: "video",
    icon: Film,
    group: "Main Hero",
    desc: "현재 `/` MainHero 배경 영상. 비우면 /videos/animated-paper-boat.webm 을 사용합니다. Legacy Spline Hero 영상과 별개입니다.",
    usage: "현재 메인 Hero",
    anchor: "/#hero",
  },
  {
    key: "media_automation_image",
    label: "업무 자동화 섹션 이미지",
    accept: "image",
    icon: ImageIcon,
    group: "Sections",
    desc: "메인 Automation 섹션 이미지.",
    usage: "메인 Automation 섹션",
    anchor: "/#automation",
  },
  {
    key: "media_ai_platform_image",
    label: "AI 플랫폼 섹션 이미지",
    accept: "image",
    icon: ImageIcon,
    group: "Sections",
    desc: "메인 AI Platform 섹션 이미지.",
    usage: "메인 AI Platform 섹션",
    anchor: "/#ai-platform",
  },
  {
    key: "media_about_video",
    label: "회사소개 페이지 영상",
    accept: "video",
    icon: Film,
    group: "회사소개",
    desc: "/about WHO WE ARE 영상. 비우면 /videos/about-section.mp4",
    usage: "회사소개 페이지",
    anchor: "/about#about",
  },
  {
    key: "media_og_image",
    label: "OG / 공유 이미지",
    accept: "image",
    icon: ImageIcon,
    group: "SEO / OG",
    desc: "카카오·SNS 공유 이미지. 1200×630 권장.",
    usage: "Open Graph",
    anchor: "/",
  },
  {
    key: "media_hero_badge_icon",
    label: "Legacy Hero 뱃지 아이콘",
    accept: "image",
    icon: ImageIcon,
    group: "Legacy",
    desc: "이전 Spline Hero 전용. 현재 `/`에는 사용되지 않습니다. /?embed=1 미리보기만.",
    usage: "Legacy Preview",
    anchor: "/?embed=1",
  },
  {
    key: "media_hero_bg_image",
    label: "Legacy Hero 배경 이미지",
    accept: "image",
    icon: ImageIcon,
    group: "Legacy",
    desc: "이전 Spline Hero 대체 배경. 현재 MainHero와 무관합니다.",
    usage: "Legacy Preview",
    anchor: "/?embed=1",
  },
  {
    key: "media_hero_bg_video",
    label: "Legacy Hero 배경 영상",
    accept: "video",
    icon: Film,
    group: "Legacy",
    desc: "이전 Spline Hero 대체 영상. media_main_hero_video 와 혼용하지 않습니다.",
    usage: "Legacy Preview",
    anchor: "/?embed=1",
  },
];

const ACCEPT_RULES = {
  image: { exts: ".jpg,.jpeg,.png,.webp,.svg", category: "image", maxMB: 20 },
  gif: { exts: ".gif", category: "gif", maxMB: 50 },
  video: { exts: ".mp4,.webm,.mov", category: "video", maxMB: 200 },
};

const SLOT_GROUP_ORDER = ["브랜드", "Main Hero", "Sections", "회사소개", "SEO / OG", "Legacy"];

const MEDIA_SECTIONS = [
  { id: "brand", label: "브랜드", groups: ["브랜드"], testId: "media-view-slots" },
  { id: "hero", label: "Main Hero", groups: ["Main Hero"] },
  { id: "main", label: "Sections", groups: ["Sections"] },
  { id: "etc", label: "회사소개", groups: ["회사소개"] },
  { id: "seo", label: "SEO / OG", groups: ["SEO / OG"] },
  { id: "legacy", label: "Legacy", groups: ["Legacy"] },
  { id: "library", label: "라이브러리", groups: [], testId: "media-view-library" },
];

const SLOT_GROUP_HINTS = {
  "브랜드": "심볼·워드마크·파비콘은 서로 다른 파일입니다.",
  "Main Hero": "현재 홈 `/` 배경 영상만 이 슬롯입니다. 비우면 paper-boat.webm fallback.",
  "Legacy": "이전 Spline Hero 전용. 현재 홈에는 연결되지 않습니다. /?embed=1 미리보기만 사용합니다.",
};

function absUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("/")) return url;
  return `${BACKEND_BASE}${url}`;
}

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileNameFromUrl(url) {
  if (!url) return "";
  try {
    const path = String(url).split("?")[0];
    return decodeURIComponent(path.split("/").pop() || "") || "";
  } catch {
    return String(url);
  }
}

const PREVIEW_CAP = {
  media_favicon: { maxW: 64, maxH: 64 },
  media_logo: { maxW: 96, maxH: 96 },
  media_hero_badge_icon: { maxW: 96, maxH: 96 },
  media_footer_wordmark: { maxW: 420, maxH: 80 },
  default: { maxW: 480, maxH: 220 },
};

function fitContain(nw, nh, maxW, maxH) {
  if (!nw || !nh) return { width: Math.min(maxW, 120), height: "auto" };
  const scale = Math.min(1, maxW / nw, maxH / nh);
  return {
    width: Math.max(1, Math.round(nw * scale)),
    height: Math.max(1, Math.round(nh * scale)),
  };
}

function Preview({ url, category, className = "", slotKey, variant = "slot", onNaturalSize }) {
  const [nat, setNat] = useState(null);
  if (!url) {
    return (
      <div className={`admin-media-empty ${className}`}>
        파일 없음
      </div>
    );
  }
  const src = absUrl(url);
  if (category === "video") {
    return (
      <video
        src={src}
        controls
        preload="metadata"
        className={`admin-media-preview admin-media-preview--video bg-black object-contain ${className}`}
        data-testid="media-preview-video"
      />
    );
  }
  if (variant === "thumb") {
    return (
      <img
        src={src}
        alt=""
        className={`admin-media-preview admin-media-preview--thumb ${className}`}
        data-testid="media-preview-image"
      />
    );
  }
  const cap = PREVIEW_CAP[slotKey] || PREVIEW_CAP.default;
  const size = nat ? fitContain(nat.w, nat.h, cap.maxW, cap.maxH) : { maxWidth: cap.maxW, maxHeight: cap.maxH };
  return (
    <div className="admin-media-preview-frame" data-testid="media-preview-frame">
      <img
        src={src}
        alt=""
        onLoad={(e) => {
          const next = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight };
          setNat(next);
          onNaturalSize?.(next);
        }}
        style={{ ...size, objectFit: "contain" }}
        className={`admin-media-preview ${className}`}
        data-testid="media-preview-image"
      />
    </div>
  );
}

function SlotMeta({ url, nat, usage }) {
  if (!url) return null;
  return (
    <div className="admin-media-meta" data-testid="media-slot-meta">
      <div>현재 파일: {fileNameFromUrl(url) || "-"}</div>
      <div>원본 크기: {nat ? `${nat.w} × ${nat.h}` : "읽는 중…"}</div>
      {usage ? <div>사용 위치: {usage}</div> : null}
    </div>
  );
}

function SlotCard({ slot, currentUrl, library, onAssign, onUpload }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nat, setNat] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setNat(null);
  }, [currentUrl]);

  const rules = ACCEPT_RULES[slot.accept];
  const compatible = (library || []).filter((m) => m.category === rules.category);

  const handleFile = async (file) => {
    if (!file) return;
    const maxBytes = rules.maxMB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`최대 ${rules.maxMB}MB 까지 업로드 가능합니다.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await onUpload(file, rules.category);
      await onAssign(slot.key, uploaded.url);
      toast.success("미리보기에 반영됨 · 저장해야 DEV에 적용됩니다");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "업로드 실패");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid={`media-slot-${slot.key}`}
      className={`bg-[#0a0a0a] border rounded-2xl p-5 transition-colors ${
        dragOver ? "border-[#0055FF]" : "border-white/10"
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        handleFile(f);
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <slot.icon size={14} className="text-white/50 shrink-0" />
          <span className="text-sm text-white truncate" title={slot.label}>{slot.label}</span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-white/30 shrink-0">
          {slot.accept} · max {rules.maxMB}MB
        </span>
      </div>
      {slot.desc && (
        <p className="text-[11px] text-white/50 mb-3 leading-relaxed" data-testid={`media-slot-${slot.key}-desc`}>
          {slot.desc}
        </p>
      )}

      <Preview
        url={currentUrl}
        category={slot.accept === "video" ? "video" : "image"}
        slotKey={slot.key}
        onNaturalSize={setNat}
      />
      <SlotMeta url={currentUrl} nat={nat} usage={slot.usage} />

      <input
        ref={fileRef}
        type="file"
        accept={rules.exts}
        className="hidden"
        data-testid={`media-slot-${slot.key}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className="flex gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`media-slot-${slot.key}-upload`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs disabled:opacity-50"
        >
          <Upload size={12} />
          {uploading ? "업로드 중..." : currentUrl ? "교체" : "업로드"}
        </button>
        {compatible.length > 0 && (
          <button
            onClick={() => setPickerOpen((v) => !v)}
            data-testid={`media-slot-${slot.key}-picker`}
            className="px-3 py-2 rounded-lg border border-white/10 text-white/70 hover:bg-white/5 text-xs"
            title="라이브러리에서 선택"
          >
            라이브러리 ({compatible.length})
          </button>
        )}
        {currentUrl && (
          <button
            onClick={() => onAssign(slot.key, "")}
            data-testid={`media-slot-${slot.key}-clear`}
            className="px-3 py-2 rounded-lg border border-white/10 text-white/60 hover:bg-white/5 text-xs"
            title="이 슬롯 비우기"
          >
            <X size={12} />
          </button>
        )}
        {currentUrl && (
          <a
            href={absUrl(currentUrl)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`media-slot-${slot.key}-original`}
            className="px-3 py-2 rounded-lg border border-white/10 text-white/60 hover:bg-white/5 text-xs inline-flex items-center"
            title="원본 보기"
          >
            원본 보기
          </a>
        )}
        {slot.anchor && (
          <a
            href={slot.anchor}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`media-slot-${slot.key}-view`}
            className="px-3 py-2 rounded-lg border border-white/10 text-white/60 hover:bg-white/5 text-xs inline-flex items-center"
            title="사이트에서 보기 (새 창)"
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>
      {pickerOpen && compatible.length > 0 && (
        <div
          className="mt-3 grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 rounded-lg border border-white/10 bg-black/30"
          data-testid={`media-slot-${slot.key}-picker-panel`}
        >
          {compatible.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                onAssign(slot.key, m.url);
                setPickerOpen(false);
              }}
              data-testid={`media-slot-${slot.key}-pick-${m.id}`}
              className={`relative aspect-square overflow-hidden rounded border ${
                currentUrl === m.url ? "border-[#0055FF]" : "border-white/10 hover:border-white/30"
              }`}
              title={m.original_name || m.stored}
            >
              <Preview url={m.url} category={m.category === "video" ? "video" : "image"} variant="thumb" className="w-full h-full" />
              {(m.used_by || []).length > 0 && (
                <span className="absolute top-1 left-1 text-[9px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                  사용중
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] text-white/30">파일을 카드 위로 끌어다 놓아도 됩니다.</div>
    </div>
  );
}

function LibraryItem({ item, onDelete, onAssignTo }) {
  const isUsed = (item.used_by || []).length > 0;
  return (
    <div
      data-testid={`media-library-item-${item.id}`}
      className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden flex flex-col"
    >
      <div className="h-32 w-full overflow-hidden bg-black">
        <Preview
          url={item.url}
          category={item.category === "video" ? "video" : "image"}
          variant="thumb"
          className="w-full h-32"
        />
      </div>
      <div className="p-3 text-xs space-y-1.5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-white/80" title={item.original_name}>
            {item.original_name || item.stored}
          </span>
          {isUsed ? (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 shrink-0">
              <Check size={10} /> 사용중
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 shrink-0">미사용</span>
          )}
        </div>
        <div className="text-white/40 text-[10px]">
          {item.category} · {fmtSize(item.size || 0)}
        </div>
        {isUsed && (
          <div className="text-white/40 text-[10px]">사용처: {item.used_by.join(", ")}</div>
        )}
        <div className="mt-auto pt-2 flex gap-1">
          <button
            onClick={() => onAssignTo(item)}
            data-testid={`media-library-assign-${item.id}`}
            className="flex-1 px-2 py-1 rounded-md border border-white/10 text-white/70 hover:bg-white/5 text-[11px]"
          >
            슬롯 지정
          </button>
          <button
            onClick={() => onDelete(item)}
            disabled={isUsed}
            data-testid={`media-library-delete-${item.id}`}
            className="px-2 py-1 rounded-md border border-white/10 text-red-300/80 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed text-[11px]"
            title={isUsed ? "사용 중이라 삭제할 수 없습니다" : "삭제"}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MediaTab({ onSaved }) {
  const [slots, setSlots] = useState({});
  const [draftSlots, setDraftSlots] = useState({});
  const [cmsBase, setCmsBase] = useState({});
  const [library, setLibrary] = useState([]);
  const [filter, setFilter] = useState("all");
  const [usageFilter, setUsageFilter] = useState("all");
  const [section, setSection] = useAdminQueryParam("section", "brand");
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState("");
  const view = section === "library" ? "library" : "slots";
  const activeSection = MEDIA_SECTIONS.find((s) => s.id === section) || MEDIA_SECTIONS[0];
  const dirty = JSON.stringify(draftSlots) !== JSON.stringify(slots);

  const previewSection =
    section === "hero" ? "hero"
      : section === "main" ? "sections"
        : section === "etc" ? "about-page"
          : section === "seo" ? "og"
            : section === "legacy" ? "legacy"
              : "brand";

  const load = async () => {
    try {
      const [s, l, c] = await Promise.all([
        api.get("/admin/media/slots"),
        api.get("/admin/media"),
        api.get("/cms"),
      ]);
      setSlots(s.data || {});
      setDraftSlots(s.data || {});
      setLibrary(l.data || []);
      setCmsBase(c.data || {});
    } catch (e) {
      toast.error("미디어 로드 실패");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadFile = async (file, category) => uploadMediaFile(file, category);

  const draftAssign = (slot, url) => {
    setDraftSlots((prev) => ({ ...prev, [slot]: url || "" }));
    setSavedNote("");
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const keys = Object.keys({ ...slots, ...draftSlots });
      for (const key of keys) {
        if ((draftSlots[key] || "") === (slots[key] || "")) continue;
        await api.post("/admin/media/assign", { slot: key, url: draftSlots[key] || "" });
      }
      toast.success("저장 완료 · DEV 홈페이지 반영됨");
      setSavedNote("저장 완료 · DEV 홈페이지 반영됨");
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const assignSlot = async (slot, url) => {
    draftAssign(slot, url);
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`'${item.original_name || item.stored}' 을(를) 영구 삭제할까요?`)) return;
    try {
      await api.delete(`/admin/media/${item.id}`);
      toast.success("삭제 완료");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "삭제 실패");
    }
  };

  const handleUploadInLibrary = async (file, category) => {
    try {
      await uploadFile(file, category);
      toast.success("업로드 완료");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "업로드 실패");
    }
  };

  const assignToSlotFromLibrary = async (item) => {
    const candidates = SLOTS.filter((s) => {
      if (item.category === "image") return s.accept === "image";
      if (item.category === "gif") return s.accept === "gif";
      if (item.category === "video") return s.accept === "video";
      return false;
    });
    if (candidates.length === 0) {
      toast.error("호환되는 슬롯이 없습니다.");
      return;
    }
    const choice = window.prompt(
      `슬롯을 선택하세요:\n${candidates.map((c, i) => `${i + 1}. ${c.label} (${c.key})`).join("\n")}\n번호 입력:`
    );
    const idx = parseInt(choice, 10) - 1;
    if (Number.isNaN(idx) || idx < 0 || idx >= candidates.length) return;
    await assignSlot(candidates[idx].key, item.url);
    toast.success("미리보기에 반영됨 · 저장해야 DEV에 적용됩니다");
  };

  const filtered = library.filter((it) => {
    if (filter !== "all" && it.category !== filter) return false;
    const isUsed = (it.used_by || []).length > 0;
    if (usageFilter === "used" && !isUsed) return false;
    if (usageFilter === "unused" && isUsed) return false;
    return true;
  });

  const usedCount = library.filter((i) => (i.used_by || []).length > 0).length;

  const editor = (
    <>
      <AdminSubTabs
        tabs={MEDIA_SECTIONS.map((s) => ({ id: s.id, label: s.label, testId: s.testId }))}
        value={activeSection.id}
        onChange={setSection}
      />

      {view === "slots" && (
        <div className="space-y-8">
          {activeSection.groups.filter((group) => SLOTS.some((s) => s.group === group)).map((group) => (
            <div key={group} className="space-y-3" data-testid={`media-group-${group}`}>
              <div className="border-b border-white/5 pb-2">
                <h4 className="text-xs uppercase tracking-[0.2em] text-white/40">{group}</h4>
                {SLOT_GROUP_HINTS[group] ? (
                  <p className="mt-2 text-[11px] text-white/45 leading-relaxed max-w-3xl">
                    {SLOT_GROUP_HINTS[group]}
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-4">
                {SLOTS.filter((s) => s.group === group).map((slot) => (
                  <SlotCard
                    key={slot.key}
                    slot={slot}
                    currentUrl={draftSlots[slot.key]}
                    library={library}
                    onAssign={assignSlot}
                    onUpload={uploadFile}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "library" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 justify-between bg-[#0a0a0a] border border-white/10 rounded-xl p-3">
            <div className="flex gap-1">
              {[
                { v: "all", l: "전체" },
                { v: "image", l: "이미지" },
                { v: "gif", l: "GIF" },
                { v: "video", l: "동영상" },
              ].map((b) => (
                <button
                  key={b.v}
                  onClick={() => setFilter(b.v)}
                  data-testid={`media-filter-${b.v}`}
                  className={`px-3 py-1.5 rounded-md text-xs ${
                    filter === b.v ? "bg-[#0055FF] text-white" : "text-white/60 hover:bg-white/5"
                  }`}
                >
                  {b.l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 text-xs">
              {[
                { v: "all", l: `전체 ${library.length}` },
                { v: "used", l: `사용중 ${usedCount}` },
                { v: "unused", l: `미사용 ${library.length - usedCount}` },
              ].map((b) => (
                <button
                  key={b.v}
                  onClick={() => setUsageFilter(b.v)}
                  data-testid={`media-usage-${b.v}`}
                  className={`px-3 py-1.5 rounded-md ${
                    usageFilter === b.v
                      ? "bg-white/10 text-white"
                      : "text-white/50 hover:bg-white/5"
                  }`}
                >
                  {b.l}
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-white/70 px-3 py-1.5 rounded-md border border-dashed border-white/15 hover:bg-white/5">
              <Upload size={12} />
              파일 업로드
              <input
                type="file"
                className="hidden"
                accept=".jpg,.jpeg,.png,.webp,.svg,.gif,.mp4,.webm,.mov"
                data-testid="media-library-upload"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const ext = (f.name.split(".").pop() || "").toLowerCase();
                  const cat = ["jpg", "jpeg", "png", "webp", "svg"].includes(ext)
                    ? "image"
                    : ext === "gif"
                    ? "gif"
                    : ["mp4", "webm", "mov"].includes(ext)
                    ? "video"
                    : null;
                  if (!cat) {
                    toast.error("지원하지 않는 파일 형식입니다.");
                    return;
                  }
                  handleUploadInLibrary(f, cat);
                  e.target.value = "";
                }}
              />
            </label>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-white/40 border border-dashed border-white/10 rounded-xl">
              표시할 미디어가 없습니다.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filtered.map((item) => (
                <LibraryItem
                  key={item.id}
                  item={item}
                  onDelete={deleteItem}
                  onAssignTo={assignToSlotFromLibrary}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (view === "library") {
    return (
      <div className="space-y-4" data-testid="admin-media">
        <AdminPageHeader
          title="미디어 관리"
          description="라이브러리 파일은 DEV 저장소에만 올라갑니다. 슬롯 지정은 저장 전까지 미리보기입니다."
        />
        {editor}
      </div>
    );
  }

  return (
    <CmsSplitPreview
      testId="admin-media"
      cms={{ ...cmsBase, ...draftSlots }}
      section={previewSection}
      dirty={dirty}
      savedNote={savedNote}
      header={(
        <AdminPageHeader
          title="미디어 관리"
          description="슬롯을 바꾸면 오른쪽 실제 섹션 미리보기에 바로 보입니다. 저장해야 DEV 홈에 반영됩니다."
          actions={(
            <button type="button" className="btn-primary disabled:opacity-50" disabled={saving || !dirty} onClick={saveDraft} data-testid="media-save">
              {saving ? "저장 중..." : "저장"}
            </button>
          )}
        />
      )}
      editor={editor}
    />
  );
}
